import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Alert, Spin, Tag, Row, Col, Statistic, Tabs } from 'antd';
import { loadCSV } from './utils/csvLoader';
import moment from 'moment-timezone';

const { TabPane } = Tabs;

// Helper function to convert any timestamp format to epoch milliseconds (UTC)
// Handles both epoch numbers and ISO strings
const getEpochTimestamp = (timestamp) => {
  if (typeof timestamp === 'string') {
    // Check if it's a number string (epoch)
    const epoch = parseInt(timestamp);
    if (!isNaN(epoch) && epoch > 1000000000000) { // Valid epoch (after year 2001)
      return epoch;
    }
    // Otherwise parse as ISO and convert to epoch
    // If it's GMT/UTC ISO string, convert to epoch
    return moment.utc(timestamp).valueOf();
  }
  // Number (milliseconds) - already epoch, return as is
  return timestamp;
};

// Helper to format epoch timestamp in IST for display
// Input is epoch milliseconds (UTC)
const formatIST = (timestamp) => {
  // Convert UTC epoch to IST for display (add 5:30)
  return moment.utc(timestamp).add(5, 'hours').add(30, 'minutes').format('YYYY-MM-DD HH:mm:ss');
};


const ConsumptionRateVerifier = () => {
  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState([]);
  const [processed15Min, setProcessed15Min] = useState([]);
  const [processed90Min, setProcessed90Min] = useState([]);
  const [verificationResults, setVerificationResults] = useState(null);
  const [errors, setErrors] = useState([]);

  const loadData = async () => {
    setLoading(true);
    setErrors([]);
    try {
      const [raw, proc15, proc90] = await Promise.all([
        loadCSV('/Consumption Rates Periodic/range_data_chart_raw - 2025-12-10T182352.596.csv'),
        loadCSV('/Consumption Rates Periodic/range_data_chart_processed (4).csv'),
        loadCSV('/Consumption Rates Periodic/range_data_chart_processed (3).csv')
      ]);

      setRawData(raw);
      setProcessed15Min(proc15);
      setProcessed90Min(proc90);

      // Verify the data
      verifyData(raw, proc15, proc90);
    } catch (error) {
      console.error('Error loading data:', error);
      setErrors([`Failed to load data: ${error.message}`]);
    } finally {
      setLoading(false);
    }
  };

  const verifyData = (raw, proc15, proc90) => {
    const issues = [];
    const verification = {
      rawDataCount: raw.length,
      processed15MinCount: proc15.length,
      processed90MinCount: proc90.length,
      issues: []
    };

    // Process raw data to calculate expected values
    const processedRaw = processRawData(raw);
    
    // Verify 15 min intervals
    const verification15 = verifyProcessedData(processedRaw, proc15, 15, '15 minutes');
    verification.verification15Min = verification15;

    // Verify 90 min intervals
    const verification90 = verifyProcessedData(processedRaw, proc90, 90, '90 minutes');
    verification.verification90Min = verification90;

    setVerificationResults(verification);
  };

  const processRawData = (raw) => {
    if (!raw || raw.length === 0) return [];

    const processed = [];
    let cumulativeIgnitionDuration = 0; // in milliseconds

    for (let i = 0; i < raw.length; i++) {
      const row = raw[i];
      // Get epoch timestamp (UTC milliseconds)
      const timestamp = getEpochTimestamp(row.timestamp);
      const fuelLevel = parseFloat(row.fuelLevelLiters) || 0;
      const ignition = parseInt(row.effective_ignition) || 0;
      const isIgnitionOn = ignition === 1;

      if (i === 0) {
        // First row - no duration yet
        processed.push({
          timestamp,
          fuelLevel,
          ignition,
          timeDiff: 0,
          cumulativeIgnitionDurationMs: 0,
          cumulativeIgnitionDurationHours: 0,
          intervalIgnitionDurationMs: 0,
          intervalIgnitionDurationHours: 0
        });
      } else {
        const prevRow = raw[i - 1];
        const prevTimestamp = getEpochTimestamp(prevRow.timestamp);
        const prevIgnition = parseInt(prevRow.effective_ignition) || 0;
        const prevIsIgnitionOn = prevIgnition === 1;
        const timeDiff = timestamp - prevTimestamp;

        let intervalIgnitionDuration = 0;

        // Calculate ignition duration based on rules:
        // 1. If both records have ignition = 1: add duration between them
        // 2. If first has ignition = 1 and second has ignition = 0: consider period in between as ignition on
        // 3. If first has ignition = 0 and second has ignition = 1: consider period in between as ignition off (don't add)

        if (prevIsIgnitionOn && isIgnitionOn) {
          // Both have ignition on - add duration between them
          intervalIgnitionDuration = timeDiff;
        } else if (prevIsIgnitionOn && !isIgnitionOn) {
          // Transition from on to off - consider period in between as ignition on
          intervalIgnitionDuration = timeDiff;
        } else if (!prevIsIgnitionOn && isIgnitionOn) {
          // Transition from off to on - consider period in between as ignition off (don't add)
          intervalIgnitionDuration = 0;
        } else {
          // Both off - no ignition time
          intervalIgnitionDuration = 0;
        }

        cumulativeIgnitionDuration += intervalIgnitionDuration;

        processed.push({
          timestamp,
          fuelLevel,
          ignition,
          timeDiff,
          cumulativeIgnitionDurationMs: cumulativeIgnitionDuration,
          cumulativeIgnitionDurationHours: cumulativeIgnitionDuration / (1000 * 60 * 60),
          intervalIgnitionDurationMs: intervalIgnitionDuration,
          intervalIgnitionDurationHours: intervalIgnitionDuration / (1000 * 60 * 60)
        });
      }
    }

    return processed;
  };

  const verifyProcessedData = (processedRaw, processed, intervalMinutes, intervalLabel) => {
    const issues = [];
    const comparisons = [];

    // Compare each processed interval with calculated values
    processed.forEach((procRow, index) => {
      // Get epoch timestamp from processed data (UTC)
      // Convert ISO string or epoch to UTC epoch milliseconds
      const procTimestamp = getEpochTimestamp(procRow.timestamp);

      const procFuelLevel = parseFloat(procRow.fuelLevelLiters) || 0;
      // Use totalHoursDigitalIgnition if available, otherwise fall back to totalHours
      const procTotalHours = parseFloat(procRow.totalHoursDigitalIgnition) || parseFloat(procRow.totalHours) || 0;
      const procFuelDiff = parseFloat(procRow.fuelLevelDifference) || 0;
      const procConsumptionRate = parseFloat(procRow.hoursConsumptionRate) || 0;

      // Calculate the exact interval boundaries based on processed timestamp
      // The processed timestamp is the START of the interval (in UTC epoch)
      // All timestamps are in UTC epoch milliseconds
      const intervalStart = procTimestamp;
      const intervalEnd = intervalStart + (intervalMinutes * 60 * 1000);

      // Find all raw data points strictly within this interval [start, end]
      // Both timestamps are in UTC epoch milliseconds
      // Using >= and <= to include boundary points
      const rawDataInInterval = processedRaw.filter(row => {
        const rowTime = row.timestamp; // UTC epoch
        return rowTime >= intervalStart && rowTime <= intervalEnd;
      }).sort((a, b) => a.timestamp - b.timestamp); // Ensure sorted by timestamp

      // Debug logging for first few intervals
      if (index < 5) {
        console.log(`[${intervalLabel}] Interval ${index}:`, {
          processedTimestampGMT: procRow.timestamp,
          processedTimestampIST: formatIST(procTimestamp),
          intervalStartUTC: intervalStart,
          intervalEndUTC: intervalEnd,
          intervalStartIST: formatIST(intervalStart),
          intervalEndIST: formatIST(intervalEnd),
          rawDataCount: rawDataInInterval.length,
          totalRawData: processedRaw.length,
          firstRawTimestampIST: processedRaw[0] ? formatIST(processedRaw[0].timestamp) : 'N/A',
          lastRawTimestampIST: processedRaw[processedRaw.length - 1] ? formatIST(processedRaw[processedRaw.length - 1].timestamp) : 'N/A',
          firstRawTimestampUTC: processedRaw[0] ? processedRaw[0].timestamp : 'N/A',
          lastRawTimestampUTC: processedRaw[processedRaw.length - 1] ? processedRaw[processedRaw.length - 1].timestamp : 'N/A'
        });
      }

      if (rawDataInInterval.length === 0) {
        issues.push({
          type: 'missing_data',
          interval: intervalLabel,
          timestamp: procRow.timestamp,
          message: `No raw data found within this interval. Interval: ${formatIST(intervalStart)} to ${formatIST(intervalEnd)}`
        });
        comparisons.push({
          timestamp: procRow.timestamp,
          processed: {
            fuelLevel: procFuelLevel,
            fuelDiff: procFuelDiff,
            totalHours: procTotalHours,
            consumptionRate: procConsumptionRate
          },
          expected: null,
          matches: {
            fuelDiff: false,
            hours: false,
            rate: false
          },
          error: 'No raw data in interval',
          intervalInfo: {
            start: formatIST(intervalStart),
            end: formatIST(intervalEnd)
          },
          rawGroupInfo: null // Explicitly set to null when no data
        });
        return;
      }

      // Calculate expected values from raw data in this interval
      const startFuelLevel = rawDataInInterval[0].fuelLevel;
      const endFuelLevel = rawDataInInterval[rawDataInInterval.length - 1].fuelLevel;
      
      // Recalculate ignition duration only for points within this interval
      // This ensures we're not using cumulative values that span multiple intervals
      // The rawDataInInterval already has the ignition field from processRawData
      const totalIgnitionHoursRaw = calculateIgnitionDurationForInterval(rawDataInInterval);
      
      // Debug logging for specific interval to understand the difference
      if (formatIST(procTimestamp) === '2025-11-15 00:30:00') {
        const timeSpanMs = rawDataInInterval[rawDataInInterval.length - 1].timestamp - rawDataInInterval[0].timestamp;
        const timeSpanHours = timeSpanMs / (1000 * 60 * 60);
        console.log('Debug for 00:30:00 interval:', {
          rawDataCount: rawDataInInterval.length,
          totalIgnitionHoursRaw,
          totalIgnitionHoursRounded: parseFloat(totalIgnitionHoursRaw.toFixed(2)),
          processedTotalHours: procTotalHours,
          processedTotalHoursDigital: parseFloat(procRow.totalHoursDigitalIgnition || 0),
          intervalStart: formatIST(intervalStart),
          intervalEnd: formatIST(intervalEnd),
          firstRawTimestamp: formatIST(rawDataInInterval[0].timestamp),
          lastRawTimestamp: formatIST(rawDataInInterval[rawDataInInterval.length - 1].timestamp),
          timeSpanMinutes: timeSpanMs / (1000 * 60),
          timeSpanHours: timeSpanHours,
          timeSpanHoursRounded: parseFloat(timeSpanHours.toFixed(2)),
          difference: Math.abs(procTotalHours - parseFloat(totalIgnitionHoursRaw.toFixed(2)))
        });
      }
      
      const totalIgnitionHours = totalIgnitionHoursRaw;

      const rawGroup = {
        startTime: intervalStart,
        endTime: intervalEnd,
        startFuelLevel,
        endFuelLevel,
        totalIgnitionHours,
        rowCount: rawDataInInterval.length
      };

      // Calculate expected values
      // Note: fuelLevelDifference might be negative if fuel was added (refueling)
      const expectedFuelDiffRaw = rawGroup.startFuelLevel - rawGroup.endFuelLevel;
      const expectedTotalHoursRaw = rawGroup.totalIgnitionHours;
      
      // Round fuel diff and total hours to match processed data precision
      // Using toFixed(2) and parseFloat to match standard decimal rounding behavior
      // This prevents rounding differences in consumption rate calculation
      const expectedFuelDiff = parseFloat(expectedFuelDiffRaw.toFixed(2)); // Round to 2 decimal places
      const expectedTotalHours = parseFloat(expectedTotalHoursRaw.toFixed(2)); // Round to 2 decimal places
      
      // Calculate consumption rate using rounded values to match processed calculation
      const expectedConsumptionRate = expectedTotalHours > 0 
        ? expectedFuelDiff / expectedTotalHours 
        : (expectedFuelDiff < 0 ? 0 : null); // If fuel was added, rate should be 0 or null
      
      // Round consumption rate to 2 decimal places to match processed precision
      const expectedConsumptionRateRounded = expectedConsumptionRate !== null 
        ? parseFloat(expectedConsumptionRate.toFixed(2))
        : null;

      // Compare values with tolerance
      const fuelDiffMatch = Math.abs(procFuelDiff - expectedFuelDiff) < 0.5; // Allow 0.5L tolerance
      const hoursMatch = Math.abs(procTotalHours - expectedTotalHours) < 0.05; // Allow 0.05 hours (3 min) tolerance
      const rateMatch = expectedConsumptionRateRounded === null 
        ? procConsumptionRate === 0 || isNaN(procConsumptionRate)
        : Math.abs(procConsumptionRate - expectedConsumptionRateRounded) < 0.1; // Allow 0.1 L/hr tolerance (reduced since we're rounding now)

      comparisons.push({
        timestamp: procRow.timestamp,
        processed: {
          fuelLevel: procFuelLevel,
          fuelDiff: procFuelDiff,
          totalHours: procTotalHours,
          consumptionRate: procConsumptionRate
        },
        expected: {
          fuelLevel: rawGroup.endFuelLevel,
          fuelDiff: expectedFuelDiff,
          totalHours: expectedTotalHours,
          consumptionRate: expectedConsumptionRateRounded
        },
        matches: {
          fuelDiff: fuelDiffMatch,
          hours: hoursMatch,
          rate: rateMatch
        },
        rawGroupInfo: {
          startTime: formatIST(rawGroup.startTime),
          endTime: formatIST(rawGroup.endTime),
          rowCount: rawGroup.rowCount,
          processedInterval: formatIST(procTimestamp)
        }
      });

      if (!fuelDiffMatch) {
        issues.push({
          type: 'fuel_difference_mismatch',
          interval: intervalLabel,
          timestamp: procRow.timestamp,
          processed: procFuelDiff,
          expected: expectedFuelDiff,
          difference: Math.abs(procFuelDiff - expectedFuelDiff)
        });
      }

      if (!hoursMatch) {
        issues.push({
          type: 'hours_mismatch',
          interval: intervalLabel,
          timestamp: procRow.timestamp,
          processed: procTotalHours,
          expected: expectedTotalHours,
          difference: Math.abs(procTotalHours - expectedTotalHours)
        });
      }

      if (!rateMatch && expectedTotalHours > 0) {
        issues.push({
          type: 'consumption_rate_mismatch',
          interval: intervalLabel,
          timestamp: procRow.timestamp,
          processed: procConsumptionRate,
          expected: expectedConsumptionRate,
          difference: Math.abs(procConsumptionRate - expectedConsumptionRate)
        });
      }
    });

    return {
      issues,
      comparisons,
      totalIntervals: processed.length,
      issuesCount: issues.length
    };
  };

  // Calculate ignition duration for raw data points within a specific interval
  // This recalculates ignition duration only for points within the interval boundaries
  // Returns total in hours, rounded to match processed data precision
  const calculateIgnitionDurationForInterval = (rawDataInInterval) => {
    if (!rawDataInInterval || rawDataInInterval.length === 0) return 0;

    let totalIgnitionDuration = 0; // in milliseconds

    for (let i = 0; i < rawDataInInterval.length; i++) {
      const row = rawDataInInterval[i];
      const timestamp = row.timestamp;
      const ignition = parseInt(row.ignition) || 0;
      const isIgnitionOn = ignition === 1;

      if (i === 0) {
        // First row in interval - we need to check if there's a previous row
        // that's just before the interval start to handle the transition correctly
        // For now, skip the first row's contribution as it depends on previous interval
        // We'll handle it when we process subsequent rows
        continue;
      } else {
        const prevRow = rawDataInInterval[i - 1];
        const prevTimestamp = prevRow.timestamp;
        const prevIgnition = parseInt(prevRow.ignition) || 0;
        const prevIsIgnitionOn = prevIgnition === 1;
        
        // Calculate time difference between consecutive points within the interval
        const timeDiff = timestamp - prevTimestamp;

        // Apply ignition duration rules:
        // 1. If both records have ignition = 1: add duration between them
        // 2. If first has ignition = 1 and second has ignition = 0: consider period in between as ignition on
        // 3. If first has ignition = 0 and second has ignition = 1: consider period in between as ignition off (don't add)

        if (prevIsIgnitionOn && isIgnitionOn) {
          // Both have ignition on - add duration between them
          totalIgnitionDuration += timeDiff;
        } else if (prevIsIgnitionOn && !isIgnitionOn) {
          // Transition from on to off - consider period in between as ignition on
          totalIgnitionDuration += timeDiff;
        } else if (!prevIsIgnitionOn && isIgnitionOn) {
          // Transition from off to on - consider period in between as ignition off (don't add)
          // Do nothing
        } else {
          // Both off - no ignition time
          // Do nothing
        }
      }
    }

    // Convert to hours
    const totalHours = totalIgnitionDuration / (1000 * 60 * 60);
    // Return raw value - we'll round it later to match processed data rounding behavior
    return totalHours;
  };

  useEffect(() => {
    loadData();
  }, []);

  const getConsumptionTrendData = (comparisons) => {
    if (!comparisons || comparisons.length === 0) return [];
    
    return comparisons
      .filter(c => c.expected && c.expected.consumptionRate !== null)
      .map((c, index) => {
        return {
          timestamp: c.timestamp,
          time: moment.utc(c.timestamp).tz('Asia/Kolkata').format('HH:mm'),
        consumptionRate: c.processed.consumptionRate,
        expectedRate: c.expected.consumptionRate,
        fuelDiff: c.processed.fuelDiff,
        totalHours: c.processed.totalHours,
        isHighConsumption: c.processed.consumptionRate > 50, // Flag high consumption
        isMismatch: !c.matches.rate
        };
      })
      .sort((a, b) => a.timestamp - b.timestamp);
  };

  const columns = [
    {
      title: 'Timestamp (IST)',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 200,
      render: (text) => {
        // Display timestamp in IST
        return formatIST(text);
      }
    },
    {
      title: 'Raw Data Period',
      key: 'rawGroupInfo',
      width: 250,
      render: (_, record) => {
        if (!record.rawGroupInfo) {
          // Show interval info if available, or error message
          if (record.intervalInfo) {
            return (
              <div style={{ fontSize: '11px', color: '#ff4d4f' }}>
                <div>No data found</div>
                <div style={{ color: '#999' }}>{record.intervalInfo.start}</div>
                <div style={{ color: '#999' }}>to {record.intervalInfo.end}</div>
              </div>
            );
          }
          return <span style={{ color: '#ff4d4f' }}>N/A</span>;
        }
        return (
          <div style={{ fontSize: '11px' }}>
            <div><strong>{record.rawGroupInfo.startTime}</strong></div>
            <div>to <strong>{record.rawGroupInfo.endTime}</strong></div>
            <div style={{ color: '#999' }}>{record.rawGroupInfo.rowCount} data points</div>
            {record.rawGroupInfo.processedInterval && (
              <div style={{ color: '#52c41a', fontSize: '10px', marginTop: '4px' }}>
                Processed: {record.rawGroupInfo.processedInterval}
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Fuel Diff',
      key: 'fuelDiff',
      children: [
        {
          title: 'Processed',
          dataIndex: ['processed', 'fuelDiff'],
          key: 'procFuelDiff',
          render: (val) => val?.toFixed(2) || 'N/A'
        },
        {
          title: 'Expected',
          dataIndex: ['expected', 'fuelDiff'],
          key: 'expFuelDiff',
          render: (val) => val?.toFixed(2) || 'N/A'
        },
        {
          title: 'Match',
          key: 'fuelDiffMatch',
          render: (_, record) => (
            <Tag color={record.matches.fuelDiff ? 'green' : 'red'}>
              {record.matches.fuelDiff ? '✓' : '✗'}
            </Tag>
          )
        }
      ]
    },
    {
      title: 'Total Hours',
      key: 'totalHours',
      children: [
        {
          title: 'Processed',
          dataIndex: ['processed', 'totalHours'],
          key: 'procTotalHours',
          render: (val) => val?.toFixed(2) || 'N/A'
        },
        {
          title: 'Expected',
          dataIndex: ['expected', 'totalHours'],
          key: 'expTotalHours',
          render: (val) => val?.toFixed(2) || 'N/A'
        },
        {
          title: 'Match',
          key: 'hoursMatch',
          render: (_, record) => (
            <Tag color={record.matches.hours ? 'green' : 'red'}>
              {record.matches.hours ? '✓' : '✗'}
            </Tag>
          )
        }
      ]
    },
    {
      title: 'Consumption Rate',
      key: 'consumptionRate',
      children: [
        {
          title: 'Processed',
          dataIndex: ['processed', 'consumptionRate'],
          key: 'procConsumptionRate',
          render: (val) => val?.toFixed(2) || 'N/A'
        },
        {
          title: 'Expected',
          dataIndex: ['expected', 'consumptionRate'],
          key: 'expConsumptionRate',
          render: (val) => val?.toFixed(2) || 'N/A'
        },
        {
          title: 'Match',
          key: 'rateMatch',
          render: (_, record) => (
            <Tag color={record.matches.rate ? 'green' : 'red'}>
              {record.matches.rate ? '✓' : '✗'}
            </Tag>
          )
        }
      ]
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="Consumption Rate Data Verification"
        extra={
          <Button type="primary" onClick={loadData} loading={loading}>
            Reload & Verify
          </Button>
        }
      >
        {loading && <Spin size="large" />}
        
        {errors.length > 0 && (
          <Alert
            message="Errors"
            description={errors.map((e, i) => <div key={i}>{e}</div>)}
            type="error"
            style={{ marginBottom: 16 }}
          />
        )}

        {verificationResults && (
          <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Statistic
                  title="Raw Data Points"
                  value={verificationResults.rawDataCount}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="15 Min Intervals"
                  value={verificationResults.processed15MinCount}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="90 Min Intervals"
                  value={verificationResults.processed90MinCount}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Total Issues"
                  value={
                    (verificationResults.verification15Min?.issuesCount || 0) +
                    (verificationResults.verification90Min?.issuesCount || 0)
                  }
                  valueStyle={{ color: '#cf1322' }}
                />
              </Col>
            </Row>

            <Tabs defaultActiveKey="15min">
              <TabPane tab="15 Minute Intervals" key="15min">
            {verificationResults.verification15Min && (
              <div>
                <Card
                  title="15 Minute Intervals Verification"
                  style={{ marginBottom: 16 }}
                  extra={
                    <Tag color={verificationResults.verification15Min.issuesCount === 0 ? 'green' : 'red'}>
                      {verificationResults.verification15Min.issuesCount === 0 
                        ? 'All Correct' 
                        : `${verificationResults.verification15Min.issuesCount} Issues`}
                    </Tag>
                  }
                >
                {verificationResults.verification15Min.issues.length > 0 && (
                  <Alert
                    message="Issues Found"
                    description={
                      <ul>
                        {verificationResults.verification15Min.issues.slice(0, 10).map((issue, i) => (
                          <li key={i}>
                            {issue.timestamp}: {issue.type} - 
                            Processed: {issue.processed}, Expected: {issue.expected}
                            {issue.difference && ` (Diff: ${issue.difference.toFixed(2)})`}
                          </li>
                        ))}
                        {verificationResults.verification15Min.issues.length > 10 && (
                          <li>... and {verificationResults.verification15Min.issues.length - 10} more</li>
                        )}
                      </ul>
                    }
                    type="warning"
                    style={{ marginBottom: 16 }}
                  />
                )}
                <Table
                  columns={columns}
                  dataSource={verificationResults.verification15Min.comparisons}
                  rowKey={(record, index) => `15min_${index}`}
                  pagination={{ pageSize: 20 }}
                  scroll={{ x: 1200 }}
                  size="small"
                />
                </Card>

                {/* Consumption Trend Analysis */}
                <Card
                  title="Consumption Rate Trend Analysis (15 Min)"
                  style={{ marginTop: 16 }}
                >
                  <Table
                    columns={[
                      {
                        title: 'Time',
                        dataIndex: 'time',
                        key: 'time',
                        width: 100
                      },
                      {
                        title: 'Consumption Rate (L/hr)',
                        key: 'consumptionRate',
                        children: [
                          {
                            title: 'Processed',
                            dataIndex: 'consumptionRate',
                            key: 'procRate',
                            render: (val, record) => (
                              <span style={{ 
                                color: record.isHighConsumption ? '#ff4d4f' : '#52c41a',
                                fontWeight: record.isHighConsumption ? 'bold' : 'normal'
                              }}>
                                {val?.toFixed(2) || 'N/A'}
                              </span>
                            )
                          },
                          {
                            title: 'Expected',
                            dataIndex: 'expectedRate',
                            key: 'expRate',
                            render: (val) => val?.toFixed(2) || 'N/A'
                          },
                          {
                            title: 'Status',
                            key: 'status',
                            render: (_, record) => (
                              <Tag color={record.isMismatch ? 'red' : 'green'}>
                                {record.isMismatch ? 'Mismatch' : 'Match'}
                              </Tag>
                            )
                          }
                        ]
                      },
                      {
                        title: 'Fuel Diff (L)',
                        dataIndex: 'fuelDiff',
                        key: 'fuelDiff',
                        render: (val) => val?.toFixed(2) || 'N/A'
                      },
                      {
                        title: 'Total Hours',
                        dataIndex: 'totalHours',
                        key: 'totalHours',
                        render: (val) => val?.toFixed(2) || 'N/A'
                      }
                    ]}
                    dataSource={getConsumptionTrendData(verificationResults.verification15Min.comparisons)}
                    rowKey={(record, index) => `trend15_${index}`}
                    pagination={{ pageSize: 20 }}
                    size="small"
                  />
                </Card>
              </div>
            )}
              </TabPane>
              <TabPane tab="90 Minute Intervals" key="90min">
            {verificationResults.verification90Min && (
              <div>
                <Card
                  title="90 Minute Intervals Verification"
                  extra={
                    <Tag color={verificationResults.verification90Min.issuesCount === 0 ? 'green' : 'red'}>
                      {verificationResults.verification90Min.issuesCount === 0 
                        ? 'All Correct' 
                        : `${verificationResults.verification90Min.issuesCount} Issues`}
                    </Tag>
                  }
                >
                {verificationResults.verification90Min.issues.length > 0 && (
                  <Alert
                    message="Issues Found"
                    description={
                      <ul>
                        {verificationResults.verification90Min.issues.slice(0, 10).map((issue, i) => (
                          <li key={i}>
                            {issue.timestamp}: {issue.type} - 
                            Processed: {issue.processed}, Expected: {issue.expected}
                            {issue.difference && ` (Diff: ${issue.difference.toFixed(2)})`}
                          </li>
                        ))}
                        {verificationResults.verification90Min.issues.length > 10 && (
                          <li>... and {verificationResults.verification90Min.issues.length - 10} more</li>
                        )}
                      </ul>
                    }
                    type="warning"
                    style={{ marginBottom: 16 }}
                  />
                )}
                <Table
                  columns={columns}
                  dataSource={verificationResults.verification90Min.comparisons}
                  rowKey={(record, index) => `90min_${index}`}
                  pagination={{ pageSize: 20 }}
                  scroll={{ x: 1200 }}
                  size="small"
                />
                </Card>

                {/* Consumption Trend Analysis */}
                <Card
                  title="Consumption Rate Trend Analysis (90 Min)"
                  style={{ marginTop: 16 }}
                >
                  <Table
                    columns={[
                      {
                        title: 'Time',
                        dataIndex: 'time',
                        key: 'time',
                        width: 100
                      },
                      {
                        title: 'Consumption Rate (L/hr)',
                        key: 'consumptionRate',
                        children: [
                          {
                            title: 'Processed',
                            dataIndex: 'consumptionRate',
                            key: 'procRate',
                            render: (val, record) => (
                              <span style={{ 
                                color: record.isHighConsumption ? '#ff4d4f' : '#52c41a',
                                fontWeight: record.isHighConsumption ? 'bold' : 'normal'
                              }}>
                                {val?.toFixed(2) || 'N/A'}
                              </span>
                            )
                          },
                          {
                            title: 'Expected',
                            dataIndex: 'expectedRate',
                            key: 'expRate',
                            render: (val) => val?.toFixed(2) || 'N/A'
                          },
                          {
                            title: 'Status',
                            key: 'status',
                            render: (_, record) => (
                              <Tag color={record.isMismatch ? 'red' : 'green'}>
                                {record.isMismatch ? 'Mismatch' : 'Match'}
                              </Tag>
                            )
                          }
                        ]
                      },
                      {
                        title: 'Fuel Diff (L)',
                        dataIndex: 'fuelDiff',
                        key: 'fuelDiff',
                        render: (val) => val?.toFixed(2) || 'N/A'
                      },
                      {
                        title: 'Total Hours',
                        dataIndex: 'totalHours',
                        key: 'totalHours',
                        render: (val) => val?.toFixed(2) || 'N/A'
                      }
                    ]}
                    dataSource={getConsumptionTrendData(verificationResults.verification90Min.comparisons)}
                    rowKey={(record, index) => `trend90_${index}`}
                    pagination={{ pageSize: 20 }}
                    size="small"
                  />
                </Card>
              </div>
            )}
              </TabPane>
            </Tabs>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ConsumptionRateVerifier;


















